type ValidationResult = {
  valid: boolean;
  error?: string;
};

export const validateVideo = async (url: string): Promise<ValidationResult> => {
  const getVideoMetadata = (
    url: string
  ): Promise<{
    duration: number;
    width: number;
    height: number;
    mimeType: string;
  }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';
      video.src = url;

      video.onloadedmetadata = () => {
        resolve({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          mimeType: url.endsWith('.mov') ? 'video/quicktime' : 'video/mp4',
        });
      };

      video.onerror = () => {
        reject(new Error('Failed to load video'));
      };
    });
  };

  const MAX_SIZE_BYTES = 8 * 1024 * 1024;

  try {
    const response = await fetch(url);
    const contentLength = response.headers.get('content-length');
    const fileSize = contentLength ? parseInt(contentLength, 10) : NaN;

    if (!fileSize || isNaN(fileSize)) {
      return { valid: false, error: 'Could not determine video size' };
    }

    if (fileSize > MAX_SIZE_BYTES) {
      return { valid: false, error: 'Video exceeds 8MB size limit' };
    }

    const { duration, width, height, mimeType } = await getVideoMetadata(url);

    if (!['video/mp4', 'video/quicktime'].includes(mimeType)) {
      return { valid: false, error: 'Invalid file type. Only MP4 or MOV allowed.' };
    }

    if (![15, 20, 30].includes(Math.round(duration))) {
      return { valid: false, error: 'Video duration must be 15, 20, or 30 seconds.' };
    }

    if (width !== 1920 || height !== 1080) {
      return { valid: false, error: 'Video resolution must be 1920x1080.' };
    }

    return { valid: true };
  } catch (err) {
    return { valid: false, error: 'Failed to validate video file' };
  }
};
